<template>
  <div class="space-y-6">
    <!-- En-tête avec bouton d'ajout et recherche -->
    <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
      <div>
        <h2 class="text-2xl font-extrabold text-blue-900 dark:text-gray-100">Gestion des Utilisateurs</h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">Gérez les utilisateurs et leurs permissions</p>
      </div>
      
      <div class="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
        <!-- Barre de recherche -->
        <div class="relative flex-1 sm:flex-initial">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            @input="handleSearch"
            placeholder="Rechercher par nom, email, téléphone, rôle..."
            class="pl-10 pr-4 py-2 w-full lg:w-80 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          >
        </div>

        <!-- Filtre statut -->
        <select 
          v-model="statusFilter"
          @change="fetchUsers"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous les statuts</option>
          <option value="1">Activés</option>
          <option value="0">Désactivés</option>
        </select>

        <button 
          v-if="$canAny(['users-create', 'users-all'])"
          @click="openModal"
          class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-all duration-200 flex items-center shadow-sm hover:shadow-md whitespace-nowrap"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Ajouter un Utilisateur
        </button>
      </div>
    </div>

    <!-- Tableau des utilisateurs - Version Responsive -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <!-- Version Desktop - Tableau complet -->
      <div class="hidden xl:block">
        <div class="overflow-x-auto">
          <table class="w-full min-w-full">
            <thead class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Informations
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Contact
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Rôle
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Statut
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Permissions
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Gammes
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Motifs
                </th>
                <th class="px-4 py-3 text-right text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
              <tr 
                v-for="user in paginatedUsers" 
                :key="user.id" 
                class="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors duration-150 group"
              >
                <!-- Nom et Email -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                      {{ getUserInitials(user.name) }}
                    </div>
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[150px]">{{ user.name }}</div>
                      <div class="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[150px]">{{ user.email }}</div>
                    </div>
                  </div>
                </td>

                <!-- Contact -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="space-y-1">
                    <div v-if="user.telephone" class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <svg class="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                      </svg>
                      <span class="truncate max-w-[120px]">{{ user.telephone }}</span>
                    </div>
                    <div v-if="user.wilaya" class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <svg class="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      <span class="truncate max-w-[120px]">{{ user.wilaya }}</span>
                    </div>
                    <div v-if="!user.telephone && !user.wilaya" class="text-sm text-gray-400 italic">
                      Aucune information
                    </div>
                  </div>
                </td>
                
                <!-- Rôle -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="flex flex-wrap gap-1">
                    <span 
                      v-for="role in user.roles" 
                      :key="role.id"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium truncate max-w-[120px]"
                      :class="getRoleBadgeClass(role.name)"
                    >
                      {{ role.name }}
                    </span>
                  </div>
                </td>
                
                <!-- Statut Activation -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <button
                    @click="toggleUserActivation(user)"
                    :disabled="togglingUserId === user.id"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
                    :class="user.activer ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"
                  >
                    <span 
                      class="inline-block h-4 w-4 transform bg-white rounded-full transition-transform duration-200"
                      :class="user.activer ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                  <span class="ml-2 text-sm text-gray-600 dark:text-gray-300">
                    {{ user.activer ? 'Activé' : 'Désactivé' }}
                  </span>
                </td>
                
                <!-- Permissions Directes -->
                <td class="px-4 py-3">
                  <div class="max-w-[200px]">
                    <div class="text-sm text-gray-600 dark:text-gray-300 truncate">
                      {{ getUserDirectPermissions(user) }}
                    </div>
                    <div v-if="userHasAdministrateurRole(user)" class="text-xs text-green-600 dark:text-green-400 mt-1">
                      (Toutes les permissions)
                    </div>
                  </div>
                </td>
                
                <!-- Gammes -->
                <td class="px-4 py-3">
                  <div class="max-w-[150px]">
                    <div class="text-sm text-gray-600 dark:text-gray-300 truncate">
                      {{ getUserGammes(user) }}
                    </div>
                  </div>
                </td>
                
                <!-- Motifs -->
                <td class="px-4 py-3">
                  <div class="max-w-[150px]">
                    <div class="text-sm text-gray-600 dark:text-gray-300 truncate">
                      {{ getUserMotifs(user) }}
                    </div>
                  </div>
                </td>
                
                <!-- Actions -->
                <td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex justify-end space-x-1">
                    <button 
                      @click="editUser(user)"
                      class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 group/btn"
                      title="Modifier"
                      :disabled="togglingUserId === user.id"
                    >
                      <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button 
                      @click="deleteUser(user.id)"
                      class="p-2 text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200 group/btn"
                      title="Supprimer"
                      :disabled="togglingUserId === user.id"
                    >
                      <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Version Tablette - Colonnes réduites -->
      <div class="hidden lg:block xl:hidden">
        <div class="overflow-x-auto">
          <table class="w-full min-w-full">
            <thead class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Informations
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Rôle
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Statut
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Permissions
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Affectations
                </th>
                <th class="px-4 py-3 text-right text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
              <tr 
                v-for="user in paginatedUsers" 
                :key="user.id" 
                class="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors duration-150 group"
              >
                <!-- Nom et Email -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                      {{ getUserInitials(user.name) }}
                    </div>
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[120px]">{{ user.name }}</div>
                      <div class="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[120px]">{{ user.email }}</div>
                      <div v-if="user.telephone" class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                        📞 {{ user.telephone }}
                      </div>
                    </div>
                  </div>
                </td>
                
                <!-- Rôle -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="flex flex-wrap gap-1">
                    <span 
                      v-for="role in user.roles" 
                      :key="role.id"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium truncate max-w-[100px]"
                      :class="getRoleBadgeClass(role.name)"
                    >
                      {{ role.name }}
                    </span>
                  </div>
                </td>
                
                <!-- Statut Activation -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <button
                    @click="toggleUserActivation(user)"
                    :disabled="togglingUserId === user.id"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
                    :class="user.activer ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"
                  >
                    <span 
                      class="inline-block h-4 w-4 transform bg-white rounded-full transition-transform duration-200"
                      :class="user.activer ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </td>
                
                <!-- Permissions Directes -->
                <td class="px-4 py-3">
                  <div class="max-w-[150px]">
                    <div class="text-sm text-gray-600 dark:text-gray-300 truncate">
                      {{ getUserDirectPermissions(user) }}
                    </div>
                    <div v-if="userHasAdministrateurRole(user)" class="text-xs text-green-600 dark:text-green-400 mt-1">
                      (Toutes)
                    </div>
                  </div>
                </td>
                
                <!-- Affectations combinées -->
                <td class="px-4 py-3">
                  <div class="space-y-1 max-w-[150px]">
                    <div v-if="getUserGammes(user) !== 'Aucune'" class="text-xs text-gray-600 dark:text-gray-300 truncate">
                      <span class="font-medium">Gammes:</span> {{ getUserGammes(user) }}
                    </div>
                    <div v-if="getUserMotifs(user) !== 'Aucun'" class="text-xs text-gray-600 dark:text-gray-300 truncate">
                      <span class="font-medium">Motifs:</span> {{ getUserMotifs(user) }}
                    </div>
                    <div v-if="getUserGammes(user) === 'Aucune' && getUserMotifs(user) === 'Aucun'" class="text-xs text-gray-400 italic">
                      Aucune affectation
                    </div>
                  </div>
                </td>
                
                <!-- Actions -->
                <td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex justify-end space-x-1">
                    <button 
                      @click="editUser(user)"
                      class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200"
                      title="Modifier"
                      :disabled="togglingUserId === user.id"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button 
                      @click="deleteUser(user.id)"
                      class="p-2 text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200"
                      title="Supprimer"
                      :disabled="togglingUserId === user.id"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Version Mobile - Cartes -->
      <div class="lg:hidden">
        <div 
          v-for="user in paginatedUsers" 
          :key="user.id"
          class="border-b border-gray-200 dark:border-gray-600 last:border-b-0 p-4 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
        >
          <!-- En-tête mobile -->
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center min-w-0 flex-1">
              <div class="flex-shrink-0 h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                {{ getUserInitials(user.name) }}
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{{ user.name }}</div>
                <div class="text-sm text-gray-600 dark:text-gray-300 truncate">{{ user.email }}</div>
              </div>
            </div>
            <div class="flex space-x-1 ml-2">
              <!-- Toggle Activation -->
              <button
                @click="toggleUserActivation(user)"
                :disabled="togglingUserId === user.id"
                class="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 rounded-lg transition-all duration-200"
                :title="user.activer ? 'Désactiver' : 'Activer'"
              >
                <svg v-if="user.activer" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
              
              <button 
                @click="editUser(user)"
                class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200"
                title="Modifier"
                :disabled="togglingUserId === user.id"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button 
                @click="deleteUser(user.id)"
                class="p-2 text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200"
                title="Supprimer"
                :disabled="togglingUserId === user.id"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Informations détaillées -->
          <div class="grid grid-cols-1 gap-3 text-sm">
            <!-- Contact -->
            <div class="flex items-center justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Contact:</span>
              <div class="text-right">
                <div v-if="user.telephone" class="text-gray-700 dark:text-gray-300">{{ user.telephone }}</div>
                <div v-if="user.wilaya" class="text-gray-700 dark:text-gray-300">{{ user.wilaya }}</div>
                <div v-if="!user.telephone && !user.wilaya" class="text-gray-400 italic">Aucune information</div>
              </div>
            </div>

            <!-- Rôle -->
            <div class="flex items-center justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Rôle:</span>
              <div class="flex flex-wrap gap-1 justify-end">
                <span 
                  v-for="role in user.roles" 
                  :key="role.id"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="getRoleBadgeClass(role.name)"
                >
                  {{ role.name }}
                </span>
              </div>
            </div>

            <!-- Statut -->
            <div class="flex items-center justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Statut:</span>
              <div class="flex items-center">
                <button
                  @click="toggleUserActivation(user)"
                  :disabled="togglingUserId === user.id"
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2 mr-2"
                  :class="user.activer ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"
                >
                  <span 
                    class="inline-block h-3 w-3 transform bg-white rounded-full transition-transform duration-200"
                    :class="user.activer ? 'translate-x-4' : 'translate-x-1'"
                  />
                </button>
                <span class="text-sm text-gray-700 dark:text-gray-300">
                  {{ user.activer ? 'Activé' : 'Désactivé' }}
                </span>
              </div>
            </div>

            <!-- Permissions -->
            <div class="flex items-start justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Permissions:</span>
              <div class="text-right max-w-32">
                <div class="text-gray-700 dark:text-gray-300 truncate">
                  {{ getUserDirectPermissions(user) }}
                </div>
                <div v-if="userHasAdministrateurRole(user)" class="text-xs text-green-600 dark:text-green-400">
                  (Toutes les permissions)
                </div>
              </div>
            </div>

            <!-- Gammes -->
            <div class="flex items-start justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Gammes:</span>
              <div class="text-right max-w-32">
                <div class="text-gray-700 dark:text-gray-300 truncate">
                  {{ getUserGammes(user) }}
                </div>
              </div>
            </div>

            <!-- Motifs -->
            <div class="flex items-start justify-between">
              <span class="text-gray-500 dark:text-gray-400 font-medium">Motifs:</span>
              <div class="text-right max-w-32">
                <div class="text-gray-700 dark:text-gray-300 truncate">
                  {{ getUserMotifs(user) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- État de chargement -->
      <div v-if="loading" class="p-8 text-center">
        <div class="inline-flex items-center space-x-3">
          <svg class="animate-spin h-6 w-6 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-400 font-medium">Chargement des utilisateurs...</span>
        </div>
      </div>

      <!-- Message vide -->
      <div v-if="!loading && users.length === 0" class="p-12 text-center">
        <div class="max-w-sm mx-auto">
          <div class="h-20 w-20 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="h-10 w-10 text-blue-900 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Aucun utilisateur trouvé</h3>
          <p class="text-gray-500 dark:text-gray-400 mb-4">Commencez par créer votre premier utilisateur.</p>
          <button 
            @click="openModal"
            class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors inline-flex items-center"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Créer un utilisateur
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && users.length > 0" class="px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
            <span>Total: </span>
            <span class="font-semibold text-blue-900 dark:text-blue-300">{{ totalItems }}</span>
            <span>utilisateurs</span>
          </div>

          <div class="flex flex-col sm:flex-row items-center gap-4">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700 dark:text-gray-300">Afficher :</span>
              <select 
                v-model="itemsPerPage" 
                @change="currentPage = 1"
                class="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>

            <div class="flex items-center space-x-1">
              <!-- Boutons de pagination -->
              <button
                @click="currentPage = 1"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <div class="flex items-center space-x-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="currentPage = page"
                  :class="[
                    'px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 min-w-8',
                    currentPage === page 
                      ? 'bg-blue-900 text-white shadow-sm' 
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  ]"
                >
                  {{ page }}
                </button>
                
                <span v-if="hasMorePages" class="px-2 text-gray-500 dark:text-gray-400">...</span>
              </div>

              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage = totalPages"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier un utilisateur -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl transform transition-all duration-300 scale-95 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-600">
        <!-- En-tête de la modal -->
        <div class="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  {{ isEditing ? 'Modifier l\'Utilisateur' : 'Ajouter un Utilisateur' }}
                </h3>
                <p class="text-blue-100 text-sm">
                  {{ isEditing ? 'Mettez à jour les informations de l\'utilisateur' : 'Créez un nouvel utilisateur avec ses permissions' }}
                </p>
              </div>
            </div>
            <button
              @click="closeModal"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group"
            >
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenu de la modal -->
        <div class="p-6">
          <form @submit.prevent="submitForm">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Colonne de gauche - Informations de base -->
              <div class="space-y-6">
                <!-- Section Informations de base -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Informations de base
                  </h4>
                  
                  <div class="space-y-4">
                    <div>
                      <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        v-model="form.name"
                        required
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                        placeholder="Entrez le nom complet"
                        :disabled="submitting"
                      >
                    </div>
                    
                    <div>
                      <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Adresse email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        v-model="form.email"
                        required
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                        placeholder="entrez@exemple.com"
                        :disabled="submitting"
                      >
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="telephone" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          id="telephone"
                          v-model="form.telephone"
                          class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                          placeholder="Ex: 0550123456"
                          :disabled="submitting"
                        >
                      </div>

                      <div>
                        <label for="wilaya" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Wilaya
                        </label>
                        <input
                          type="text"
                          id="wilaya"
                          v-model="form.wilaya"
                          class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                          placeholder="Ex: Alger"
                          :disabled="submitting"
                        >
                      </div>
                    </div>
                    
                    <div>
                      <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {{ isEditing ? 'Nouveau mot de passe' : 'Mot de passe *' }}
                      </label>
                      <input
                        type="password"
                        id="password"
                        v-model="form.password"
                        :required="!isEditing"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                        :placeholder="isEditing ? 'Laissez vide pour ne pas changer' : 'Créez un mot de passe sécurisé'"
                        :disabled="submitting"
                      >
                      <p v-if="!isEditing" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Le mot de passe doit contenir au moins 8 caractères
                      </p>
                    </div>

                    <!-- Activation -->
                    <div class="flex items-center justify-between pt-2">
                      <label for="activer" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Activer le compte
                      </label>
                      <button
                        type="button"
                        @click="form.activer = !form.activer"
                        :disabled="submitting"
                        class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
                        :class="form.activer ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"
                      >
                        <span 
                          class="inline-block h-4 w-4 transform bg-white rounded-full transition-transform duration-200"
                          :class="form.activer ? 'translate-x-6' : 'translate-x-1'"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Section Rôle -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                    Rôle principal
                  </h4>
                  
                  <div class="space-y-3">
                    <div 
                      v-for="role in availableRoles" 
                      :key="role.id"
                      class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-600 transition-colors duration-200 cursor-pointer"
                      :class="{
                        'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700': selectedRole === role.id
                      }"
                      @click="selectedRole = role.id"
                    >
                      <input
                        type="radio"
                        :id="`role-${role.id}`"
                        :value="role.id"
                        v-model="selectedRole"
                        class="rounded-full border-gray-300 text-blue-900 focus:ring-blue-900"
                        :disabled="submitting"
                      >
                      <label 
                        :for="`role-${role.id}`" 
                        class="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer flex-1"
                      >
                        {{ role.name }}
                      </label>
                      <span 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getRoleBadgeClass(role.name)"
                      >
                        {{ role.name }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Colonne de droite - Permissions et affectations -->
              <div class="space-y-6">
                <!-- Section Permissions Directes -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                  <div class="flex items-center justify-between mb-4">
                    <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                      </svg>
                      Permissions Directes
                    </h4>
                    <div class="flex items-center space-x-2">
                      <span class="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-1 rounded-full">
                        {{ form.permissions.length }}/{{ availablePermissions.length }}
                      </span>
                      <button
                        type="button"
                        @click="toggleAllPermissions"
                        class="px-3 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors font-medium"
                        :disabled="hasAdministrateurRole || submitting"
                      >
                        {{ areAllPermissionsSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
                      </button>
                    </div>
                  </div>

                  <div v-if="hasAdministrateurRole" class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div class="flex items-center text-green-800 dark:text-green-300">
                      <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span class="text-sm font-medium">Toutes les permissions sont accordées automatiquement</span>
                    </div>
                  </div>

                  <div class="space-y-4 max-h-96 overflow-y-auto">
                    <!-- Groupe par ressource -->
                    <div v-for="(group, resource) in groupedPermissions" :key="resource" class="border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 overflow-hidden">
                      <!-- En-tête du groupe -->
                      <div class="bg-gray-50 dark:bg-gray-600 px-4 py-3 border-b border-gray-200 dark:border-gray-500">
                        <h5 class="font-semibold text-gray-900 dark:text-gray-100 text-sm capitalize">
                          {{ getResourceDisplayName(resource) }}
                        </h5>
                      </div>
                      
                      <!-- Permissions du groupe -->
                      <div class="p-3 space-y-2">
                        <!-- Lecteur (view + all) -->
                        <div class="flex items-center justify-between">
                          <label class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              :checked="isPermissionGroupSelected(group.lecteur)"
                              @change="togglePermissionGroup(group.lecteur, $event.target.checked)"
                              class="rounded border-gray-300 text-blue-900 focus:ring-blue-900 mr-3"
                              :disabled="submitting || hasAdministrateurRole"
                            >
                            <span class="font-medium">Lecteur</span>
                            <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Voir)</span>
                          </label>
                        </div>

                        <!-- Écriture (create + update) -->
                        <div class="flex items-center justify-between">
                          <label class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              :checked="isPermissionGroupSelected(group.ecriture)"
                              @change="togglePermissionGroup(group.ecriture, $event.target.checked)"
                              class="rounded border-gray-300 text-blue-900 focus:ring-blue-900 mr-3"
                              :disabled="submitting || hasAdministrateurRole"
                            >
                            <span class="font-medium">Écriture</span>
                            <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Créer + Modifier)</span>
                          </label>
                        </div>

                        <!-- Suppression (delete) -->
                        <div class="flex items-center justify-between">
                          <label class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              :checked="isPermissionGroupSelected(group.suppression)"
                              @change="togglePermissionGroup(group.suppression, $event.target.checked)"
                              class="rounded border-gray-300 text-blue-900 focus:ring-blue-900 mr-3"
                              :disabled="submitting || hasAdministrateurRole"
                            >
                            <span class="font-medium">Suppression</span>
                            <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Supprimer)</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div v-if="availablePermissions.length === 0" class="p-6 text-center border border-gray-200 dark:border-gray-600 rounded-lg">
                      <svg class="h-8 w-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Aucune permission disponible</p>
                    </div>
                  </div>
                </div>

                <!-- Sections Gammes et Motifs (conditionnelles) -->
                <div v-if="hasResponsableRole" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Gammes -->
                  <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                    <h4 class="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                      <svg class="h-4 w-4 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                      </svg>
                      Gammes assignées
                    </h4>
                    
                    <div class="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                      <div 
                        v-for="gamme in availableGammes" 
                        :key="gamme.id"
                        class="flex items-center p-2 border-b border-gray-100 dark:border-gray-600 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <input
                          type="checkbox"
                          :id="`gamme-${gamme.id}`"
                          :value="gamme.id"
                          v-model="form.selectedGammes"
                          class="rounded border-gray-300 text-blue-900 focus:ring-blue-900"
                          :disabled="submitting"
                        >
                        <label 
                          :for="`gamme-${gamme.id}`" 
                          class="ml-2 text-sm text-gray-700 dark:text-gray-300 flex-1 cursor-pointer"
                        >
                          {{ gamme.name }}
                        </label>
                      </div>
                      <div v-if="availableGammes.length === 0" class="p-4 text-center">
                        <p class="text-xs text-gray-500 dark:text-gray-400">Aucune gamme disponible</p>
                      </div>
                    </div>
                  </div>

                  <!-- Motifs -->
                  <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                    <h4 class="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                      <svg class="h-4 w-4 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                      </svg>
                      Motifs assignés
                    </h4>
                    
                    <div class="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                      <div 
                        v-for="motif in availableMotifs" 
                        :key="motif.id"
                        class="flex items-center p-2 border-b border-gray-100 dark:border-gray-600 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <input
                          type="checkbox"
                          :id="`motif-${motif.id}`"
                          :value="motif.id"
                          v-model="form.selectedMotifs"
                          class="rounded border-gray-300 text-blue-900 focus:ring-blue-900"
                          :disabled="submitting"
                        >
                        <label 
                          :for="`motif-${motif.id}`" 
                          class="ml-2 text-sm text-gray-700 dark:text-gray-300 flex-1 cursor-pointer"
                        >
                          {{ motif.name }}
                        </label>
                      </div>
                      <div v-if="availableMotifs.length === 0" class="p-4 text-center">
                        <p class="text-xs text-gray-500 dark:text-gray-400">Aucun motif disponible</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Actions de la modal -->
            <div class="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                @click="closeModal"
                class="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 font-medium disabled:opacity-50 rounded-xl"
                :disabled="submitting"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="px-6 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl hover:from-blue-800 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                :disabled="submitting || !selectedRole"
              >
                <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ submitting ? 'Traitement...' : (isEditing ? 'Mettre à jour' : 'Créer l\'utilisateur') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UsersContent',
  data() {
    return {
      users: [],
      availableRoles: [],
      availableGammes: [],
      availableMotifs: [],
      availablePermissions: [],
      loading: false,
      submitting: false,
      showModal: false,
      isEditing: false,
      currentUserId: null,
      selectedRole: null,
      togglingUserId: null,
      searchQuery: '',
      statusFilter: '',
      searchTimeout: null,
      form: {
        name: '',
        email: '',
        telephone: '',
        wilaya: '',
        password: '',
        activer: true,
        selectedGammes: [],
        selectedMotifs: [],
        permissions: []
      },
      // Pagination
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
    hasResponsableRole() {
      if (!this.selectedRole || !this.availableRoles.length) return false;
      const selectedRoleObj = this.availableRoles.find(role => role.id === this.selectedRole);
      return selectedRoleObj && selectedRoleObj.name.toLowerCase().includes('responsable');
    },

    hasAdministrateurRole() {
      if (!this.selectedRole || !this.availableRoles.length) return false;
      const selectedRoleObj = this.availableRoles.find(role => role.id === this.selectedRole);
      return selectedRoleObj && selectedRoleObj.name.toLowerCase().includes('administrateur');
    },

    areAllPermissionsSelected() {
      if (this.availablePermissions.length === 0) return false;
      return this.form.permissions.length === this.availablePermissions.length;
    },
    
    // Grouper les permissions disponibles
    groupedPermissions() {
      return this.groupPermissions(this.availablePermissions);
    },
    
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.users.slice(start, end);
    },
    
    totalItems() {
      return this.users.length;
    },
    
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisiblePages = 5;
      
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    hasMorePages() {
      return this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
    }
  },
  watch: {
    users() {
      this.currentPage = 1;
    },
    selectedRole(newRoleId) {
      if (newRoleId) {
        const selectedRole = this.availableRoles.find(role => role.id === newRoleId);
        if (selectedRole) {
          this.handleRoleChange(selectedRole);
        }
      }
    }
  },
  mounted() {
    this.fetchUsers();
    this.fetchAvailableData();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.searchQuery) {
          params.append('search', this.searchQuery);
        }
        if (this.statusFilter !== '') {
          params.append('activer', this.statusFilter);
        }

        const response = await fetch(`/users?${params.toString()}`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          this.users = data || [];
        } else {
          this.showError('Erreur lors du chargement des utilisateurs');
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur de connexion');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchUsers();
      }, 500);
    },

    async fetchAvailableData() {
      try {
        const [rolesResponse, gammesResponse, motifsResponse, permissionsResponse] = await Promise.all([
          fetch('/roles', {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }),
          fetch('/gammes', {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }),
          fetch('/motifs', {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }),
          fetch('/permissions', {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
        ]);

        if (rolesResponse.ok) {
          this.availableRoles = await rolesResponse.json();
        }

        if (gammesResponse.ok) {
          const gammesData = await gammesResponse.json();
          this.availableGammes = gammesData.data || gammesData || [];
        }

        if (motifsResponse.ok) {
          const motifsData = await motifsResponse.json();
          this.availableMotifs = motifsData.data || motifsData || [];
        }

        if (permissionsResponse.ok) {
          this.availablePermissions = await permissionsResponse.json();
        }

        // Formater les permissions
        this.availablePermissions = this.availablePermissions.map(perm => ({
          id: perm.id || perm.permission_id,
          name: perm.name || perm.permission_name
        })).filter(perm => perm.id && perm.name);
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    },

    // Nouvelle méthode pour grouper les permissions par ressource
    groupPermissions(permissions) {
      const groups = {};
      
      permissions.forEach(permission => {
        const name = permission.name || '';
        
        // Extraire la ressource et l'action
        const parts = name.split('-');
        if (parts.length < 2) return;
        
        const resource = parts[0];
        const action = parts[1];
        
        if (!groups[resource]) {
          groups[resource] = {
            lecteur: [],   // view + all
            ecriture: [],  // create + update
            suppression: [] // delete
          };
        }
        
        // Classer les permissions dans les groupes appropriés
        if (action === 'view' || action === 'all' || action === 'read') {
          groups[resource].lecteur.push(permission.id);
        } else if (action === 'create' || action === 'update') {
          groups[resource].ecriture.push(permission.id);
        } else if (action === 'delete') {
          groups[resource].suppression.push(permission.id);
        }
      });
      
      return groups;
    },

    // Vérifier si un groupe de permissions est sélectionné
    isPermissionGroupSelected(permissionIds) {
      if (!permissionIds || permissionIds.length === 0) return false;
      return permissionIds.every(id => this.form.permissions.includes(id));
    },

    // Basculer un groupe de permissions
    togglePermissionGroup(permissionIds, checked) {
      if (!permissionIds || permissionIds.length === 0) return;
      
      if (checked) {
        // Ajouter les permissions manquantes
        permissionIds.forEach(id => {
          if (!this.form.permissions.includes(id)) {
            this.form.permissions.push(id);
          }
        });
      } else {
        // Retirer toutes les permissions du groupe
        this.form.permissions = this.form.permissions.filter(
          id => !permissionIds.includes(id)
        );
      }
    },

    // Obtenir le nom d'affichage de la ressource
    getResourceDisplayName(resource) {
      const names = {
        'users': 'utilisateurs',
        'gammes': 'gammes',
        'motifs': 'motifs',
        'tickets': 'tickets',
        'user': 'utilisateurs',
        'gamme': 'gammes',
        'motif': 'motifs',
        'ticket': 'tickets',
      };
      
      return names[resource] || resource;
    },

    // Grouper les permissions d'un utilisateur pour l'affichage dans le tableau
    getUserGroupedPermissions(user) {
      const permissions = user.permissions || user.direct_permissions || [];
      return this.groupPermissions(permissions);
    },

    // Vérifier si un utilisateur a des permissions
    hasUserPermissions(user) {
      const permissions = user.permissions || user.direct_permissions || [];
      return permissions.length > 0;
    },

    toggleAllPermissions() {
      if (this.areAllPermissionsSelected) {
        this.form.permissions = [];
      } else {
        this.form.permissions = this.availablePermissions.map(permission => permission.id);
      }
    },

    async toggleUserActivation(user) {
      if (this.togglingUserId === user.id) return;
      
      this.togglingUserId = user.id;
      try {
        const token = this.getCsrfToken();
        
        // Utiliser PATCH au lieu de POST pour une meilleure sémantique
        const response = await fetch(`/users/${user.id}/toggle-activation`, {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        console.log('Toggle activation response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('Toggle activation response data:', data);
          
          // Mettre à jour l'état local immédiatement
          user.activer = data.activer;
          this.showSuccess(data.message);
          
          // Recharger les données pour s'assurer de la synchronisation
          this.fetchUsers();
        } else {
          let errorMessage = 'Erreur lors de la modification du statut';
          try {
            const data = await response.json();
            errorMessage = data.error || data.message || errorMessage;
          } catch (e) {
            console.error('Erreur parsing error response:', e);
          }
          this.showError(errorMessage);
          
          // Recharger les données pour récupérer l'état correct
          this.fetchUsers();
        }
      } catch (error) {
        console.error('Erreur complète toggle activation:', error);
        this.showError('Erreur de connexion: ' + error.message);
        
        // Recharger les données en cas d'erreur
        this.fetchUsers();
      } finally {
        this.togglingUserId = null;
      }
    },

    openModal() {
      this.isEditing = false;
      this.currentUserId = null;
      this.selectedRole = null;
      this.form = { 
        name: '', 
        email: '', 
        telephone: '',
        wilaya: '',
        password: '', 
        activer: true,
        selectedGammes: [], 
        selectedMotifs: [],
        permissions: []
      };
      this.showModal = true;
    },

    async editUser(user) {
      this.isEditing = true;
      this.currentUserId = user.id;
      
      console.log('Utilisateur à éditer:', user);
      
      if (this.availableGammes.length === 0 || this.availableMotifs.length === 0 || this.availablePermissions.length === 0) {
        await this.fetchAvailableData();
      }
      
      await this.$nextTick();
      
      const gammes = user.gammes_responsable || user.responsable_gammes || user.gammesResponsable || [];
      const motifs = user.motifs_responsable || user.responsable_motifs || user.motifsResponsable || [];
      
      let userPermissions = [];
      if (user.permissions) {
        userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
      } else if (user.direct_permissions) {
        userPermissions = Array.isArray(user.direct_permissions) ? user.direct_permissions : [];
      }
      
      console.log('Permissions utilisateur:', userPermissions);
      
      const userRoles = user.roles ? user.roles.map(role => role.id) : [];
      const mainRole = userRoles.length > 0 ? userRoles[0] : null;
      
      this.selectedRole = mainRole;
      
      const permissionIds = userPermissions.map(permission => {
        if (permission && typeof permission === 'object' && permission.id) {
          return permission.id;
        }
        return permission;
      }).filter(id => id != null);
      
      console.log('IDs de permissions à assigner:', permissionIds);
      
      this.form = {
        name: user.name,
        email: user.email,
        telephone: user.telephone || '',
        wilaya: user.wilaya || '',
        password: '',
        activer: user.activer !== undefined ? user.activer : true,
        selectedGammes: gammes.map(g => g.id || g),
        selectedMotifs: motifs.map(m => m.id || m),
        permissions: permissionIds
      };
      
      console.log('Form data final:', this.form);
      this.showModal = true;
      
      await this.$nextTick();
      console.log('Permissions dans le form après rendu:', this.form.permissions);
    },

    closeModal() {
      this.showModal = false;
      this.isEditing = false;
      this.currentUserId = null;
      this.selectedRole = null;
      this.form = { 
        name: '', 
        email: '', 
        telephone: '',
        wilaya: '',
        password: '', 
        activer: true,
        selectedGammes: [], 
        selectedMotifs: [],
        permissions: []
      };
      this.submitting = false;
    },

    async submitForm() {
      this.submitting = true;
      try {
        const url = this.isEditing ? `/users/${this.currentUserId}` : '/users';
        const method = this.isEditing ? 'PUT' : 'POST';

        const formData = {
          name: this.form.name,
          email: this.form.email,
          telephone: this.form.telephone,
          wilaya: this.form.wilaya,
          activer: this.form.activer,
          roles: [this.selectedRole],
          permissions: this.form.permissions,
          ...(this.form.password && { password: this.form.password })
        };

        if (this.hasResponsableRole) {
          formData.selectedGammes = this.form.selectedGammes;
          formData.selectedMotifs = this.form.selectedMotifs;
        } else {
          formData.selectedGammes = [];
          formData.selectedMotifs = [];
        }

        console.log('Données envoyées:', formData);

        const token = this.getCsrfToken();

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(formData)
        });

        console.log('Statut HTTP:', response.status);
        
        let data;
        try {
          data = await response.json();
        } catch (e) {
          console.error('Erreur parsing JSON:', e);
          throw new Error('Réponse invalide du serveur');
        }

        console.log('Réponse serveur:', data);

        if (response.ok) {
          this.showSuccess(this.isEditing ? 'Utilisateur modifié avec succès' : 'Utilisateur créé avec succès');
          this.closeModal();
          this.fetchUsers();
        } else {
          let errorMessage = 'Une erreur est survenue';
          
          if (data.errors) {
            const errorMessages = Object.values(data.errors).flat().join(', ');
            errorMessage = 'Erreur de validation: ' + errorMessages;
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          }
          
          console.error('Erreur détaillée:', data);
          this.showError(errorMessage);
        }
      } catch (error) {
        console.error('Erreur complète:', error);
        this.showError('Erreur de connexion: ' + error.message);
      } finally {
        this.submitting = false;
      }
    },

    handleRoleChange(selectedRole) {
      console.log('Rôle sélectionné:', selectedRole);
      
      if (selectedRole.name.toLowerCase().includes('administrateur')) {
        this.form.permissions = this.availablePermissions.map(permission => permission.id);
        console.log('Toutes les permissions sélectionnées pour Administrateur:', this.form.permissions);
      } else {
        if (!this.isEditing) {
          this.form.permissions = [];
        }
        console.log('Permissions actuelles:', this.form.permissions);
      }
    },

    userHasAdministrateurRole(user) {
      if (!user.roles || !user.roles.length) return false;
      return user.roles.some(role => role.name.toLowerCase().includes('administrateur'));
    },

    async deleteUser(id) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
        try {
          const token = this.getCsrfToken();
          
          const response = await fetch(`/users/${id}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            this.showSuccess('Utilisateur supprimé avec succès');
            this.fetchUsers();
          } else {
            const data = await response.json();
            this.showError(data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur:', error);
          this.showError('Erreur lors de la suppression');
        }
      }
    },

    // Méthodes d'affichage améliorées
    getRoleBadgeClass(roleName) {
      const classes = {
        'Administrateur': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        'Responsable': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'Utilisateur': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      };
      
      return classes[roleName] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    },

    getUserInitials(name) {
      if (!name) return '??';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },

    getPermissionDisplayName(permissionName) {
      // Cette méthode n'est plus utilisée pour l'affichage groupé
      // Mais gardez-la pour d'autres usages éventuels
      const parts = permissionName.split('-');
      if (parts.length < 2) return permissionName;
      
      const resource = this.getResourceDisplayName(parts[0]);
      const action = parts[1];
      
      const actionNames = {
        'create': 'Créer',
        'read': 'Voir',
        'update': 'Modifier',
        'delete': 'Supprimer',
        'all': 'Accès complet',
        'view': 'Voir'
      };
      
      return `${actionNames[action] || action} ${resource}`;
    },

    getUserDirectPermissions(user) {
      const permissions = user.permissions || user.direct_permissions || [];
      if (!permissions.length) return 'Aucune permission directe';
      
      // Utiliser l'affichage groupé pour une meilleure lisibilité
      const grouped = this.getUserGroupedPermissions(user);
      const permissionStrings = [];
      
      Object.keys(grouped).forEach(resource => {
        const group = grouped[resource];
        const actions = [];
        if (group.lecteur && group.lecteur.length > 0) actions.push('Lecteur');
        if (group.ecriture && group.ecriture.length > 0) actions.push('Écriture');
        if (group.suppression && group.suppression.length > 0) actions.push('Suppression');
        
        if (actions.length > 0) {
          permissionStrings.push(`${this.getResourceDisplayName(resource)}: ${actions.join(', ')}`);
        }
      });
      
      return permissionStrings.slice(0, 2).join('; ') + 
             (permissionStrings.length > 2 ? '...' : '');
    },

    getUserGammes(user) {
      const gammes = user.gammes_responsable || user.responsable_gammes || user.gammesResponsable || [];
      if (!gammes.length) return 'Aucune';
      return gammes.map(g => g.name).slice(0, 2).join(', ') +
             (gammes.length > 2 ? '...' : '');
    },

    getUserMotifs(user) {
      const motifs = user.motifs_responsable || user.responsable_motifs || user.motifsResponsable || [];
      if (!motifs.length) return 'Aucun';
      return motifs.map(m => m.name).slice(0, 2).join(', ') +
             (motifs.length > 2 ? '...' : '');
    },

    // Méthodes utilitaires
    getCsrfToken() {
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }

      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (cookieValue) {
        return decodeURIComponent(cookieValue);
      }

      if (window.Laravel && window.Laravel.csrfToken) {
        return window.Laravel.csrfToken;
      }

      console.warn('CSRF token non trouvé');
      return '';
    },

    showSuccess(message) {
      alert('Succès: ' + message);
    },

    showError(message) {
      alert('Erreur: ' + message);
    }
  }
};
</script>